import { useTranslation } from "react-i18next";

type Props = {
  gradings: Array<any>;
  maxTotalScore: any;
};

// Relative results are weighted shares of the total, so a service contributes
// result * weight / 100. Absolute results are point values and are shown as they are.
const isRelative = (grading: any) => grading.resultType === "RELATIVE";

const formatNumber = (value: number) => {
  return Number.isFinite(value) ? value.toFixed(2) : "";
};

export const GradingBreakdown = (props: Props) => {
  const { t } = useTranslation();
  const maximum = parseFloat(props.maxTotalScore);
  const gradings = (props.gradings ?? []).filter((grading: any) => grading.result !== null);

  if (gradings.length === 0) {
    return null;
  }

  const allRelative = gradings.every(isRelative);
  const weightSum = gradings.reduce((sum: number, grading: any) => sum + Number(grading.weight ?? 0), 0);
  // Shares only add up to the score while the weights add up to a full hundred percent.
  const showsShare = allRelative && weightSum === 100;
  const weightsIncomplete = allRelative && weightSum !== 100;

  const getRow = (grading: any) => {
    const result = Number(grading.result);
    const weight = Number(grading.weight ?? 0);
    return (
      <tr key={"Grading" + grading.id}>
        <td>{grading.gradingservice?.name ?? ""}</td>
        <td className="has-text-right">
          {isRelative(grading) ? Math.round(result * 100) + " %" : formatNumber(result)}
        </td>
        <td className="has-text-right">{weight ? weight + " %" : ""}</td>
        {showsShare && (
          <td className="has-text-right">
            {isNaN(maximum) ? "" : formatNumber((result * weight * maximum) / 100)}
          </td>
        )}
      </tr>
    );
  };

  return (
    <details className="mt-4">
      <summary className="is-size-7 has-text-grey" style={{ cursor: "pointer" }}>
        {t("gradingBreakdown")}
      </summary>
      <table className="table is-narrow is-fullwidth is-size-7 mt-2">
        <thead className="is-italic">
          <tr>
            <td>{t("gradingService")}</td>
            <td className="has-text-right">{t("gradingBreakdownResult")}</td>
            <td className="has-text-right">{t("gradingBreakdownWeight")}</td>
            {showsShare && <td className="has-text-right">{t("gradingBreakdownPoints")}</td>}
          </tr>
        </thead>
        <tbody>{gradings.map(getRow)}</tbody>
      </table>
      <p className={"help" + (weightsIncomplete ? " has-text-danger" : "")}>
        {showsShare && t("gradingBreakdownHint")}
        {weightsIncomplete && t("gradingBreakdownHintWeights") + " " + weightSum + " %."}
        {!allRelative && t("gradingBreakdownHintAbsolute")}
      </p>
    </details>
  );
};
